// File: DashboardModules.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import internationalization from 'src/shared/i18n/i18n';

import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { BarPlot } from '@mui/x-charts/BarChart';
import type { AllSeriesType } from '@mui/x-charts/models/seriesType';

import { Box, Divider, Grid, Link, List, ListItem, ListItemText, Typography } from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PeopleIcon from '@mui/icons-material/People';
import { ListItemAvatarWrapper } from 'src/pages/dashboard/helpers/AccountBalanceTempDemo';
import { ONLINE_WAREHOUSE_IDS, STORE_WAREHOUSE_IDS } from 'src/shared/environment';

/* ----------------------------------------------------------------------------
 * 1) TYPES (shared between main + providers)
 * ---------------------------------------------------------------------------*/

export type Period = 'Week' | 'Month' | 'Year';
export type SalesChannel = 'Store' | 'Online';
export type SalesRep = { id: string; name: string };

export type StandardSeriesKeys =
  | 'ordersStore' | 'ordersOnline'
  | 'ordersValueStore' | 'ordersValueOnline'
  | 'purchasesValueStore' | 'purchasesValueOnline'
  | 'toShipStore' | 'toShipOnline'
  | 'shippedStore' | 'shippedOnline'
  | 'returnsStore' | 'returnsOnline';

export type ChartRequest = {
  period: Period;
  selectedBars: Array<'ordersBar' | 'ordersValueBar' | 'purchasesValueBar' | 'toShipBar' | 'shippedBar' | 'returnsBar'>;
  selectedChannels: SalesChannel[];
  byRep: boolean;
  selectedReps: string[];
  allReps: SalesRep[];
  orientation: 'vertical' | 'horizontal';
};

export interface ChartDataProvider {
  getSalesRepSeries(req: ChartRequest, labels: string[]): Record<string, number[]>;
  getStandardBuckets(req: ChartRequest, labels: string[]): Record<StandardSeriesKeys, number[]>;
}

/* ----------------------------------------------------------------------------
 * 2) DATE WINDOW + LABEL HELPERS
 * ---------------------------------------------------------------------------*/

const pad2 = (n: number) => (n < 10 ? `0${n}` : String(n));
const ymd = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const ym = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

const startOfISOWeek = (d: Date) => {
  const dd = new Date(d);
  const day = (dd.getDay() + 6) % 7;
  dd.setHours(0, 0, 0, 0);
  dd.setDate(dd.getDate() - day);
  return dd;
};

const addDays = (d: Date, n: number) => { const dd = new Date(d); dd.setDate(dd.getDate() + n); return dd; };
const addMonths = (d: Date, n: number) => { const dd = new Date(d); dd.setMonth(dd.getMonth() + n); return dd; };
const addWeeks = (d: Date, n: number) => addDays(d, n * 7);

const isoWeekYearAndNumber = (date: Date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((+d - +yearStart) / 86400000 + 1) / 7);
  return { year: d.getUTCFullYear(), week: weekNo };
};

export const isoWeekKey = (d: Date) => {
  const { year, week } = isoWeekYearAndNumber(d);
  return `${year}-W${pad2(week)}`;
};

export const getLabels = (period: Period, clock: Date = new Date()): string[] => {
  const now = new Date(clock);
  now.setHours(0, 0, 0, 0);

  if (period === 'Week') {
    const start = addDays(now, -7);
    return Array.from({ length: 8 }, (_, i) => ymd(addDays(start, i)));
  }

  if (period === 'Month') {
    const curWeekStart = startOfISOWeek(now);
    const weeks = Array.from({ length: 5 }, (_, i) => addWeeks(curWeekStart, -4 + i));
    return weeks.map(isoWeekKey);
  }

  const startMonth = addMonths(now, -12);
  const startAnchor = new Date(startMonth.getFullYear(), startMonth.getMonth(), 1);
  return Array.from({ length: 13 }, (_, i) => ym(addMonths(startAnchor, i)));
};

export const labelForDate = (d: Date, period: Period, labels: string[]): string => {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);

  if (period === 'Week') {
    const key = ymd(dt);
    return labels.includes(key) ? key : '';
  }

  if (period === 'Month') {
    const key = isoWeekKey(dt);
    return labels.includes(key) ? key : '';
  }

  const key = ym(dt);
  return labels.includes(key) ? key : '';
};

export const getDisplayLabels = (period: Period, labels: string[]): string[] => {
  if (period === 'Week') {
    const wk = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return labels.map(key => {
      const [y, m, d] = key.split('-').map(Number);
      const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
      const day = wk[dt.getDay()];
      return `${day} ${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}`;
    });
  }

  if (period === 'Month') {
    return labels.map(key => {
      const [y, m, d] = key.split('-').map(Number);
      const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
      return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}`;
    });
  }

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  return labels.map(k => {
    const [yy, mm] = k.split('-').map(Number);
    const mon = months[Math.max(0, Math.min(11, (mm ?? 1) - 1))];
    const shortY = String(yy % 100).padStart(2, '0');
    return `${mon} ${shortY}`;
  });
};

export const sumArray = (arr: number[]) => arr.reduce((s, v) => s + (v || 0), 0);
export const zeroes = (n: number) => Array.from({ length: n }, () => 0);

/* ----------------------------------------------------------------------------
 * 2.5) OUTSIDE LEGEND
 * ---------------------------------------------------------------------------*/

type LegendItem = { id: string; label: string; color?: string };

const toLegendItems = (series: AllSeriesType[]): LegendItem[] => {
  return (series || []).filter(s => (s as any)?.type === 'bar').map(s => ({ id: String((s as any).id ?? ''), label: String((s as any).label ?? (s as any).id ?? ''), color: (s as any).color as string | undefined })).filter(i => i.id && i.label);
};

const OutsideLegend = React.forwardRef<HTMLDivElement, { series: AllSeriesType[] }>(({ series }, ref) => {
  const items = useMemo(() => toLegendItems(series), [series]);
  if (!items.length) return null;

  return (
    <Box ref={ref} sx={{ px: 1, pt: 1, pb: 0.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {items.map(it => (
          <Box key={it.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, minWidth: 150, maxWidth: 360 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '3px', backgroundColor: it.color ?? 'currentColor', flex: '0 0 auto' }} />
            <Typography variant="caption" noWrap title={it.label} sx={{ maxWidth: 320 }}>{it.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
});
OutsideLegend.displayName = 'OutsideLegend';

/* ----------------------------------------------------------------------------
 * 3) ORDERS-ONLY DATA PROVIDER
 * ---------------------------------------------------------------------------*/

export type OrderStatusKV = { Key: number; Value: 'Draft' | 'Fulfilling' | 'Fulfilled' | string };
export type KeyValue = { Key: number; Value: string };

export type SalesOrderRow = { Id: number; RowNumber: number; ArticleNumber: string; SalesItemId: number; SalesItemName: string; };
export type SalesOrder = {
  SalesOrderId: number;
  Status: OrderStatusKV;
  SalesOrganization: string;
  CustomerNumber: number;
  CustomerAccount: string;
  Currency: KeyValue;
  ShippingMethod: KeyValue;
  CreateDate: string;
  ReleaseDate: string | null;
  FulfillDate: string | null;
  TotalAmountNet: number;
  TotalAmountToPayGross: number;
  TotalAmountGross: number;
  TotalTaxAmount: number;
  ExternalOrderNr: string | null;
  TaxIncluded: boolean;
  PaymentMethod: KeyValue;
  PaymentTerms: KeyValue;
  SalesOrderRows: SalesOrderRow[];
};

export type DashboardSalesRow = {
  Currency: string;
  TotalAmount: number;
  TotalAmountNet: number;
  Count: number;
  SalesDate: string;
  OrderChannelType: number;
};

export type DashboardReturnsRow = {
  ReturnCreateDate: string;
  ReturnUnit: string;
  OrderChannelType: { Key: number; Value: string };
  Count: number;
};

export type DashboardDispatchedRow = {
  DispatchOrderType: { Key: number; Value: string };
  ExternalOrderChannelType: { Key: number; Value: string };
  WarehouseId: number;
  WarehouseName: string;
  Day: string;
  CountOrders: number;
};

export type DashboardToShipRow = {
  DispatchOrderType: { Key: number; Value: string };
  ExternalOrderChannelType: { Key: number; Value: string };
  WarehouseId: number;
  WarehouseName: string;
  Day: string;
  CountOrders: number;
};

export type DashboardPurchasingRow = {
  PurchaseOrderCreated: string;
  PurchaseOrderType: { Key: number; Value: string };
  OrderValueInDefaultCurreny: number;
  Count: number;
};

const STORE_CUSTOMER = 'kontant kassakund';
const normalize = (v: unknown) => String(v ?? '').trim().toLowerCase();

export const isStoreOrder = (o: any) => normalize(o?.CustomerAccount ?? "") === STORE_CUSTOMER;

export function inferChannel(o: any): SalesChannel {
  return isStoreOrder(o) ? 'Store' : 'Online';
}

export function inferSalesChannelFromOrderChannelType(orderChannelType: number): SalesChannel | null {
  if (orderChannelType === 1) return 'Online';
  if (orderChannelType === 2) return 'Online';
  if (orderChannelType === 3) return 'Store';
  return null;
}

export function inferSalesChannelFromOrderChannel(value: any): SalesChannel | null {
  if (typeof value === 'number') return inferSalesChannelFromOrderChannelType(value);
  const key = Number(value?.Key ?? value?.key ?? value?.OrderChannelType ?? value);
  if (Number.isFinite(key)) return inferSalesChannelFromOrderChannelType(key);

  const text = String(value?.Value ?? value?.value ?? '').trim().toLowerCase();
  if (text === 'web' || text === 'direct' || text === 'online') return 'Online';
  if (text === 'store' || text === 'butik') return 'Store';

  return null;
}



export const isOnlineWarehouse = (warehouseId: number) => {
  return ONLINE_WAREHOUSE_IDS.includes(warehouseId);
}
export const isStoreWarehouse = (warehouseId: number) => STORE_WAREHOUSE_IDS.includes(warehouseId);

// NOTE: Sales/order aggregation now comes from the dedicated dashboard sales endpoint.
// Keep this provider isolated so returns / fulfillment / purchases can be migrated the same way later.
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const monthKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;

const normalizeDate = (value: string | Date) => {
  const dt = new Date(value);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

export const getRollingDashboardLabels = (period: Period, clock: Date = new Date()): string[] => {
  const now = normalizeDate(clock);

  if (period === 'Week') {
    const start = addDays(now, -6);
    return Array.from({ length: 7 }, (_, i) => ymd(addDays(start, i)));
  }

  if (period === 'Month') {
    const start = addDays(now, -29);
    return Array.from({ length: 30 }, (_, i) => ymd(addDays(start, i)));
  }

  const currentMonthStart = startOfMonth(now);
  const startMonth = addMonths(currentMonthStart, -11);
  return Array.from({ length: 12 }, (_, i) => monthKey(addMonths(startMonth, i)));
};

export const getRollingDashboardLabelForDate = (value: string | Date, period: Period, labels: string[]): string => {
  const dt = normalizeDate(value);

  if (period === 'Year') {
    const key = monthKey(dt);
    return labels.includes(key) ? key : '';
  }

  const key = ymd(dt);
  return labels.includes(key) ? key : '';
};

export function buildDashboardSalesProvider(sales: DashboardSalesRow[] = []): ChartDataProvider {
  return {
    getSalesRepSeries: () => ({}),
    getStandardBuckets: (req) => {
      const labels = getRollingDashboardLabels(req.period);
      const byLabel = new Map<string, { ordersOnline: number; ordersStore: number; ordersValueOnline: number; ordersValueStore: number }>();
      labels.forEach(l => byLabel.set(l, { ordersOnline: 0, ordersStore: 0, ordersValueOnline: 0, ordersValueStore: 0 }));

      sales.forEach(row => {
        const lbl = getRollingDashboardLabelForDate(row.SalesDate, req.period, labels);
        if (!lbl) return;

        const channel = inferSalesChannelFromOrderChannelType(Number(row.OrderChannelType));
        if (!channel) return;

        const bucket = byLabel.get(lbl)!;
        const count = Number(row.Count ?? 0) || 0;
        const valueNet = Number(row.TotalAmountNet ?? 0) || 0;

        if (channel === 'Online') {
          bucket.ordersOnline += count;
          bucket.ordersValueOnline += valueNet;
        } else {
          bucket.ordersStore += count;
          bucket.ordersValueStore += valueNet;
        }
      });

      const pick = <K extends keyof ReturnType<typeof byLabel.get>>(k: K) => labels.map(l => (byLabel.get(l) as any)?.[k] ?? 0);
      const zeros = zeroes(labels.length);

      return {
        ordersStore: pick('ordersStore'),
        ordersOnline: pick('ordersOnline'),
        ordersValueStore: pick('ordersValueStore'),
        ordersValueOnline: pick('ordersValueOnline'),
        purchasesValueStore: zeros,
        purchasesValueOnline: zeros,
        toShipStore: zeros,
        toShipOnline: zeros,
        shippedStore: zeros,
        shippedOnline: zeros,
        returnsStore: zeros,
        returnsOnline: zeros,
      };
    },
  };
}

/* ----------------------------------------------------------------------------
 * 4) CHART RENDERER + SERIES BUILDERS
 * ---------------------------------------------------------------------------*/

export type ChartShellProps = {
  height: number;
  xLabels?: string[];
  yLabels?: string[];
  orientation: 'vertical' | 'horizontal';
  hasMoneyAxis?: boolean;
  sx?: any;
};

export const ChartShell: React.FC<ChartShellProps> = ({ height, xLabels, yLabels, orientation, hasMoneyAxis = false, sx }) => {
  const axes =
    orientation === 'vertical'
      ? { xAxis: [{ id: 'xAxis', data: xLabels ?? [], scaleType: 'band', hideTooltip: true }], yAxis: [{ id: 'quantities', label: tr('Antal'), position: 'left' } as any, ...(hasMoneyAxis ? [{ id: 'money', label: tr('SEK'), position: 'right' } as any] : [])] }
      : { yAxis: [{ id: 'yAxis', data: yLabels ?? [], scaleType: 'band', hideTooltip: true }], xAxis: [{ id: 'quantities', label: tr('Antal'), position: 'bottom' } as any, ...(hasMoneyAxis ? [{ id: 'money', label: tr('SEK'), position: 'top' } as any] : [])] };

  return (
    <ResponsiveChartContainer height={height} series={[]} xAxis={'xAxis' in axes ? (axes as any).xAxis : undefined} yAxis={'yAxis' in axes ? (axes as any).yAxis : undefined} margin={orientation === 'horizontal' ? { top: hasMoneyAxis ? 40 : 20, left: 80, right: 20, bottom: 30 } : { top: 20, left: 60, right: hasMoneyAxis ? 60 : 20, bottom: 30 }} sx={{ '& .MuiLineElement-root': { strokeWidth: 4 }, '& .MuiChartsAxis-line': { strokeWidth: 4 }, ...sx }}>
      <ChartsTooltip followCursor trigger="item" />
      <BarPlot />
      {orientation === 'vertical' ? (<><ChartsXAxis axisId="xAxis" /><ChartsYAxis axisId="quantities" />{hasMoneyAxis && <ChartsYAxis axisId="money" />}</>) : (<><ChartsYAxis axisId="yAxis" /><ChartsXAxis axisId="quantities" />{hasMoneyAxis && <ChartsXAxis axisId="money" />}</>)}
    </ResponsiveChartContainer>
  );
};

const repColors = ['#4e79a7', '#f28e2c', '#e15759', '#76b7b2', '#59a14f', '#edc948'];
const tr = (key: string, options?: any) => internationalization.t(key, options) as string;

export const buildRepSeries = (labels: string[], repData: Record<string, number[]>, req: ChartRequest, orientation: 'vertical' | 'horizontal'): AllSeriesType[] => {
  return req.allReps.filter(r => req.selectedReps.includes(r.id)).map((rep, i) => {
    const data = repData[rep.id] || Array.from({ length: labels.length }, () => 0);
    const total = sumArray(data).toLocaleString('sv-SE');
    return { type: 'bar', id: rep.id, data, label: tr('{{repName}} ({{total}} st)', { repName: rep.name, total }), color: repColors[i % repColors.length], ...(orientation === 'vertical' ? { yAxisKey: 'quantities' } : { xAxisKey: 'quantities', layout: 'horizontal' }), valueFormatter: (v) => `${(v ?? 0).toLocaleString('sv-SE')} ${tr('st')}` } as AllSeriesType;
  });
};

export const buildStandardSeries = (buckets: Record<StandardSeriesKeys, number[]>, req: ChartRequest, orientation: 'vertical' | 'horizontal'): AllSeriesType[] => {
  const include = (barKey: 'ordersBar' | 'ordersValueBar' | 'purchasesValueBar' | 'toShipBar' | 'shippedBar' | 'returnsBar') => req.selectedBars.includes(barKey);

  const mk = (id: string, label: string, data: number[], color: string, axis: 'quantities' | 'money', stack?: string): AllSeriesType => ({
    type: 'bar',
    id,
    data,
    label,
    color,
    ...(orientation === 'vertical' ? { yAxisKey: axis } : { xAxisKey: axis, layout: 'horizontal' }),
    valueFormatter: (v) => axis === 'money' ? `${(v ?? 0).toLocaleString('sv-SE')} ${tr('kr')}` : `${(v ?? 0).toLocaleString('sv-SE')} ${tr('st')}`,
    ...(stack ? { stack } : {}),
  } as AllSeriesType);

  const series: (AllSeriesType | false)[] = [
    include('ordersBar') && req.selectedChannels.includes('Store') && mk('ordersBarStore', tr('Orders Store ({{total}} st)', { total: sumArray(buckets.ordersStore).toLocaleString('sv-SE') }), buckets.ordersStore, '#1E3D59', 'quantities', 'ordersBar'),
    include('ordersBar') && req.selectedChannels.includes('Online') && mk('ordersBarOnline', tr('Orders Online ({{total}} st)', { total: sumArray(buckets.ordersOnline).toLocaleString('sv-SE') }), buckets.ordersOnline, '#94B0DA', 'quantities', 'ordersBar'),

    include('ordersValueBar') && req.selectedChannels.includes('Store') && mk('ordersValueBarStore', tr('Orders Value Store ({{total}} kr)', { total: sumArray(buckets.ordersValueStore).toLocaleString('sv-SE') }), buckets.ordersValueStore, '#2E7D32', 'money', 'ordersValueBar'),
    include('ordersValueBar') && req.selectedChannels.includes('Online') && mk('ordersValueBarOnline', tr('Orders Value Online ({{total}} kr)', { total: sumArray(buckets.ordersValueOnline).toLocaleString('sv-SE') }), buckets.ordersValueOnline, '#66BB6A', 'money', 'ordersValueBar'),

    include('purchasesValueBar') && req.selectedChannels.includes('Store') && mk('purchasesValueBarStore', tr('Purchases Value Store ({{total}} kr)', { total: sumArray(buckets.purchasesValueStore).toLocaleString('sv-SE') }), buckets.purchasesValueStore, '#8E24AA', 'money', 'purchasesValueBar'),
    include('purchasesValueBar') && req.selectedChannels.includes('Online') && mk('purchasesValueBarOnline', tr('Purchases Value Online ({{total}} kr)', { total: sumArray(buckets.purchasesValueOnline).toLocaleString('sv-SE') }), buckets.purchasesValueOnline, '#BA68C8', 'money', 'purchasesValueBar'),

    include('toShipBar') && req.selectedChannels.includes('Store') && mk('toShipBarStore', tr('To Ship Store'), buckets.toShipStore, '#AEDFF7', 'quantities', 'toShipBar'),
    include('toShipBar') && req.selectedChannels.includes('Online') && mk('toShipBarOnline', tr('To Ship Online'), buckets.toShipOnline, '#C6D8FF', 'quantities', 'toShipBar'),

    include('shippedBar') && req.selectedChannels.includes('Store') && mk('shippedBarStore', tr('Shipped Store'), buckets.shippedStore, '#1F5229', 'quantities', 'shippedBar'),
    include('shippedBar') && req.selectedChannels.includes('Online') && mk('shippedBarOnline', tr('Shipped Online'), buckets.shippedOnline, '#80BDA1', 'quantities', 'shippedBar'),

    include('returnsBar') && req.selectedChannels.includes('Store') && mk('returnsBarStore', tr('Returns Store'), buckets.returnsStore, '#8f6943', 'quantities', 'returnsBar'),
    include('returnsBar') && req.selectedChannels.includes('Online') && mk('returnsBarOnline', tr('Returns Online'), buckets.returnsOnline, '#D4A373', 'quantities', 'returnsBar'),
  ];

  return series.filter(Boolean) as AllSeriesType[];
};

/* ----------------------------------------------------------------------------
 * 5) ORDER CHART BASE
 * ---------------------------------------------------------------------------*/

export type OrderChartBaseProps = {
  period: Period;
  selectedBars: Array<'ordersBar' | 'ordersValueBar' | 'purchasesValueBar' | 'toShipBar' | 'shippedBar' | 'returnsBar'>;
  selectedSalesChannels: SalesChannel[];
  viewBySalesRep: boolean;
  selectedSalesReps: string[];
  salesRepsData: SalesRep[];
  dataProvider: ChartDataProvider;
  comparison?: boolean;
  orientation: 'vertical' | 'horizontal';
  height: number;
};

export const OrderChartBase: React.FC<OrderChartBaseProps> = ({ period, selectedBars, selectedSalesChannels, viewBySalesRep, selectedSalesReps, salesRepsData, dataProvider, comparison = false, orientation, height }) => {
  const labels = useMemo(() => getRollingDashboardLabels(period), [period]);
  const displayLabels = useMemo(() => getDisplayLabels(period, labels), [period, labels]);

  const comparisonLabels = useMemo(() => labels.map(l => tr('Prev {{label}}', { label: l })), [labels]);
  const displayComparisonLabels = useMemo(() => displayLabels.map(l => tr('Prev {{label}}', { label: l })), [displayLabels]);
  const interleave = (a: string[], b: string[]) => { const out: string[] = []; for (let i = 0; i < Math.min(a.length, b.length); i++) out.push(b[i], a[i]); return out; };

  const xLabels = useMemo(() => (comparison && !viewBySalesRep && orientation === 'vertical') ? interleave(displayLabels, displayComparisonLabels) : displayLabels, [displayLabels, displayComparisonLabels, comparison, viewBySalesRep, orientation]);

  const req: ChartRequest = { period, selectedBars, selectedChannels: selectedSalesChannels, byRep: viewBySalesRep, selectedReps: selectedSalesReps, allReps: salesRepsData, orientation };

  const repData = useMemo(() => (viewBySalesRep ? dataProvider.getSalesRepSeries(req, labels) : ({} as Record<string, number[]>)), [viewBySalesRep, dataProvider, req, labels]);
  const bucketData = useMemo(() => (!viewBySalesRep ? dataProvider.getStandardBuckets(req, labels) : ({} as any)), [viewBySalesRep, dataProvider, req, labels]);

  const series = useMemo<AllSeriesType[]>(() => {
    if (viewBySalesRep) return buildRepSeries(labels, repData, req, orientation);
    return buildStandardSeries(bucketData, req, orientation);
  }, [viewBySalesRep, labels, repData, bucketData, req, orientation]);

  const hasMoneyAxis = selectedBars.includes('ordersValueBar') || selectedBars.includes('purchasesValueBar');

  const legendRef = useRef<HTMLDivElement | null>(null);
  const [legendHeight, setLegendHeight] = useState<number>(48);

  useEffect(() => {
    const el = legendRef.current;
    if (!el) return;
    let rafId = 0;
    let ro: ResizeObserver | null = null;

    const measure = () => {
      rafId = 0;
      const h = Math.ceil(el.getBoundingClientRect().height || 0);
      setLegendHeight(prev => (Math.abs((prev || 0) - (h || 0)) <= 1 ? prev : (h || 0)));
    };

    const schedule = () => { if (rafId) return; rafId = window.requestAnimationFrame(measure); };

    schedule();

    if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(() => schedule()); ro.observe(el); }
    else { const t = window.setTimeout(() => schedule(), 50); return () => window.clearTimeout(t); }

    return () => { if (ro) ro.disconnect(); if (rafId) window.cancelAnimationFrame(rafId); };
  }, [series]);

  const chartHeight = Math.max(280, (height || 0) - legendHeight);
  const barGroupCount = useMemo(() => {
    const bars = series.filter(s => (s as any)?.type === 'bar');
    const groups = new Set(bars.map(s => String((s as any).stack ?? (s as any).id)));
    return groups.size;
  }, [series]);

  const bandAxisSizing = useMemo(() => {
    const single = barGroupCount <= 1;
    return single
      ? { categoryGapRatio: 0.7, barGapRatio: 0.2 }  // thinner bars when only one group
      : { categoryGapRatio: 0.25, barGapRatio: 0.1 }; // default-ish when multiple groups
  }, [barGroupCount]);

  return (
    <Box sx={{ width: '100%', height }}>
      <OutsideLegend ref={legendRef} series={series} />
      <ResponsiveChartContainer
        height={chartHeight}
        series={series}
        xAxis={orientation === 'vertical'
          ? [{ id: 'xAxis', data: xLabels ?? [], scaleType: 'band', hideTooltip: true, ...bandAxisSizing }]
          : [{ id: 'quantities', label: tr('Antal'), position: 'bottom' } as any, ...(hasMoneyAxis ? [{ id: 'money', label: tr('SEK'), position: 'top' } as any] : [])]}
        yAxis={orientation === 'vertical'
          ? [{ id: 'quantities', label: tr('Antal'), position: 'left' } as any, ...(hasMoneyAxis ? [{ id: 'money', label: '', position: 'right' } as any] : [])]
          : [{ id: 'yAxis', data: displayLabels ?? [], scaleType: 'band', hideTooltip: true, ...bandAxisSizing }]}
        margin={orientation === 'horizontal' ? { top: hasMoneyAxis ? 40 : 20, left: 80, right: 20, bottom: 30 } : { top: 20, left: 60, right: hasMoneyAxis ? 80 : 20, bottom: 30 }}
        sx={{ '& .MuiLineElement-root': { strokeWidth: 4 }, '& .MuiChartsAxis-line': { strokeWidth: 4 } }}
      >
        <ChartsTooltip followCursor trigger="item" />
        <BarPlot />
        {orientation === 'vertical' ? (<><ChartsXAxis axisId="xAxis" /><ChartsYAxis axisId="quantities" position="left" />{hasMoneyAxis && <ChartsYAxis axisId="money" position="right" />}</>) : (<><ChartsYAxis axisId="yAxis" /><ChartsXAxis axisId="quantities" position="bottom" />{hasMoneyAxis && <ChartsXAxis axisId="money" position="top" />}</>)}
      </ResponsiveChartContainer>
    </Box>
  );
};

/* ----------------------------------------------------------------------------
 * 6) KPI ENGINE + PRESENTATION (unchanged from your file, kept intact)
 * ---------------------------------------------------------------------------*/

export const parseEnabledAndThreshold = (entry: any) => {
  const enabled = String(entry?.enabled ?? 'false').toLowerCase() === 'true';
  const threshold = Number(entry?.threshold ?? 0);
  return { enabled, threshold };
};

export const daysInMonthForLabel = (labelYYYYMM: string): number => {
  const [yStr, mStr] = labelYYYYMM.split('-');
  const y = Number(yStr), m = Number(mStr);
  return new Date(y, m, 0).getDate();
};

type BuildKpiArgs = {
  period: Period;
  dashboardSales: DashboardSalesRow[];
  dashboardReturns: DashboardReturnsRow[];
  settingsKpi: Record<string, { enabled: string | boolean; threshold: string | number }>;
};

export type KpiEntry = {
  id: string;
  title: string;
  actualLabel?: string;
  actualDisplay?: string;
  deltaPctDisplay?: string;
  isGood?: boolean;
  icon?: 'orders' | 'money' | 'returns' | 'visitors';
  value?: number;
  target?: number;
  unit?: 'SEK' | 'st' | string;
  percent?: number;
  status?: 'success' | 'warning' | 'error';
  tooltip?: string;
};

export const buildKpiListFromSettings = (args: BuildKpiArgs): KpiEntry[] => {
  const { period, dashboardSales, dashboardReturns, settingsKpi } = args;

  const labels = getRollingDashboardLabels(period);
  if (labels.length === 0) return [];

  // NOTE: KPI order metrics now use dashboard sales aggregates instead of raw orders.
  // This keeps the KPI path aligned with the chart provider migration and the moving window behavior.
  const salesInWindow = dashboardSales.filter(row => Boolean(getRollingDashboardLabelForDate(row.SalesDate, period, labels)));

  let divisorDays = 0;
  if (period === 'Week') divisorDays = 7;
  else if (period === 'Month') divisorDays = 30;
  else divisorDays = labels.map(l => daysInMonthForLabel(l)).reduce((a, b) => a + b, 0);

  const totalOrdersCount = salesInWindow.reduce((sum, row) => sum + (Number(row.Count ?? 0) || 0), 0);
  const sumNet = salesInWindow.reduce((sum, row) => sum + (Number(row.TotalAmountNet ?? 0) || 0), 0);

  const avgDailyOrders = divisorDays > 0 ? totalOrdersCount / divisorDays : 0;
  const avgOrderValue = totalOrdersCount > 0 ? (sumNet / totalOrdersCount) : 0;

  const returnsEnabled = parseEnabledAndThreshold(settingsKpi?.ReturnsWeekly).enabled;
  const returnsThreshold = parseEnabledAndThreshold(settingsKpi?.ReturnsWeekly).threshold;
  let returnsCountCompletedWeek = 0;

  if (returnsEnabled) {
    const targetIsoWeek = (() => {
      const weeksInWindow = salesInWindow.map(row => isoWeekKey(new Date(row.SalesDate)));
      const candidate = weeksInWindow.length ? weeksInWindow.sort().pop()! : isoWeekKey(new Date());
      return candidate;
    })();

    const returnsInThatWeek = dashboardReturns.filter(row => {
      const dt = new Date(row.ReturnCreateDate);
      const lbl = isoWeekKey(dt);
      return lbl === targetIsoWeek;
    });
    returnsCountCompletedWeek = returnsInThatWeek.reduce((sum, row) => sum + (Number(row.Count ?? 0) || 0), 0);
  }

  const kpis: KpiEntry[] = [];

  {
    const { enabled, threshold } = parseEnabledAndThreshold(settingsKpi?.TotalDailyOrders);
    if (enabled) {
      const pct = threshold > 0 ? (avgDailyOrders / threshold - 1) * 100 : 0;
      kpis.push({ id: 'TotalDailyOrders', title: tr('Total Daily Orders'), actualLabel: tr('Avg/Day'), actualDisplay: `${avgDailyOrders.toLocaleString('sv-SE', { maximumFractionDigits: 2 })} ${tr('st')}`, deltaPctDisplay: `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`, isGood: avgDailyOrders >= threshold, icon: 'orders' });
    }
  }

  {
    const { enabled, threshold } = parseEnabledAndThreshold(settingsKpi?.OrderValueAvrage);
    if (enabled) {
      const pct = threshold > 0 ? (avgOrderValue / threshold - 1) * 100 : 0;
      kpis.push({ id: 'OrderValueAvrage', title: tr('Order Value Avg'), actualLabel: tr('Kr/Order'), actualDisplay: `${Math.round(avgOrderValue).toLocaleString('sv-SE')} ${tr('kr')}`, deltaPctDisplay: `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`, isGood: avgOrderValue >= threshold, icon: 'money' });
    }
  }

  if (returnsEnabled) {
    const pct = returnsThreshold > 0 ? (returnsCountCompletedWeek / returnsThreshold - 1) * 100 : 0;
    const isGood = returnsCountCompletedWeek <= returnsThreshold;
    kpis.push({ id: 'ReturnsWeekly', title: tr('Returns (weekly)'), actualLabel: tr('Qty/Week'), actualDisplay: `${returnsCountCompletedWeek.toLocaleString('sv-SE')} ${tr('st')}`, deltaPctDisplay: `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`, isGood, icon: 'returns' });
  }

  return kpis;
};

type KpiOverview2Props = { kpis?: KpiEntry[]; };

const IconFor = (kind: KpiEntry['icon']) => {
  switch (kind) {
    case 'orders': return <ShoppingCartIcon />;
    case 'money': return <MonetizationOnIcon />;
    case 'returns': return <AccountBalanceWalletIcon />;
    case 'visitors': return <PeopleIcon />;
    default: return <ShoppingCartIcon />;
  }
};

export const KpiOverview2: React.FC<KpiOverview2Props> = ({ kpis = [] }) => {
  const ActualText = ({ children, color }: any) => (<Typography variant="subtitle2" sx={{ ml: 0.5 }} color={color}>{children}</Typography>);

  if (!kpis.length) {
    return (
      <Box py={{ xs: 2, md: 4 }} pr={{ xs: 2, md: 4 }} flex={1}>
        <Typography variant="body2" color="text.secondary">{tr('No enabled KPIs.')} <Link href="/settings/webclient">{tr('Set and Enable in settings')}</Link></Typography>
      </Box>
    );
  }

  const toDisplay = (k: KpiEntry) => {
    const actualDisplay = k.actualDisplay ?? (typeof k.value === 'number' ? `${Math.round(k.value).toLocaleString('sv-SE')} ${k.unit ?? ''}`.trim() : '');
    const deltaPctDisplay = k.deltaPctDisplay ?? (typeof k.percent === 'number' ? `${k.percent >= 0 ? '+' : ''}${k.percent.toFixed(0)}%` : (typeof k.value === 'number' && typeof k.target === 'number' && k.target !== 0 ? `${(((k.value / k.target) - 1) * 100 >= 0 ? '+' : '')}${(((k.value / k.target) - 1) * 100).toFixed(0)}%` : ''));

    let color: 'success.main' | 'warning.main' | 'error.main' = 'success.main';
    if (k.status === 'warning') color = 'warning.main';
    else if (k.status === 'error') color = 'error.main';
    else if (typeof k.isGood === 'boolean') color = k.isGood ? 'success.main' : 'error.main';

    const icon = k.icon ?? (k.unit === 'SEK' ? 'money' : 'orders');
    return { actualLabel: k.actualLabel ?? '', actualDisplay, deltaPctDisplay, color, icon };
  };

  return (
    <>
      <Box component="span" sx={{ display: { xs: 'none', md: 'inline-block' } }}><Divider absolute orientation="vertical" /></Box>
      <Box py={{ xs: 2, md: 4 }} pr={{ xs: 2, md: 4 }} flex={1}>
        <Grid container spacing={0}>
          <Grid xs={12} item display="flex" alignItems="center">
            <List disablePadding sx={{ width: '100%' }}>
              {kpis.map(kpi => {
                const disp = toDisplay(kpi);
                return (
                  <ListItem key={kpi.id} disableGutters title={kpi.tooltip}>
                    <ListItemAvatarWrapper>{IconFor(disp.icon)}</ListItemAvatarWrapper>
                    <ListItemText primary={kpi.title} primaryTypographyProps={{ variant: 'h5', noWrap: true }} secondary={disp.actualLabel} secondaryTypographyProps={{ variant: 'subtitle2', noWrap: true }} />
                    <Box textAlign="right">
                      <Typography variant="h4" noWrap>{disp.actualDisplay}</Typography>
                      {disp.deltaPctDisplay && (<ActualText color={disp.color}>{disp.deltaPctDisplay}</ActualText>)}
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

/* ----------------------------------------------------------------------------
 * 7) MOBILE CARD METRICS (shared hook)
 * ---------------------------------------------------------------------------*/

export type MobileCardMetrics = {
  range: { todayLabel: string; last7Labels: string[] };
  orders: { today: { online: number; store: number; total: number }; week: { online: number; store: number; total: number }; avgPerDay: { online: number; store: number; total: number }; };
  orderValue: { today: { online: number; store: number; total: number }; week: { online: number; store: number; total: number }; avgPerDay: { online: number; store: number; total: number }; };
  returns: { today: { online: number; store: number; total: number }; week: { online: number; store: number; total: number }; avgPerDay: { online: number; store: number; total: number }; };
  purchasingLast3MonthsSEK: number;
  shipping: { shippedThisMonth: number; toShipOpen: number };
};

const startOfDayLocal = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const startOfMonthLocal = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonthsLocal = (d: Date, n: number) => { const dd = new Date(d); dd.setMonth(dd.getMonth() + n); return dd; };

const sumByLabels = (labelsPick: string[], allLabels: string[], series: number[]) => {
  const idx = new Map<string, number>();
  allLabels.forEach((l, i) => idx.set(l, i));
  return labelsPick.reduce((acc, l) => acc + (series[idx.get(l) ?? -1] ?? 0), 0);
};

const pack = (online: number, store: number) => ({ online, store, total: online + store });

export const useMobileDashboardMetrics = (args: { dataProvider: ChartDataProvider; periodForCards?: Period; dashboardPurchasing?: DashboardPurchasingRow[]; dashboardDispatched?: DashboardDispatchedRow[]; dashboardToShip?: DashboardToShipRow[]; }): MobileCardMetrics => {
  const { dataProvider, dashboardPurchasing = [], dashboardDispatched = [], dashboardToShip = [], periodForCards = 'Week' } = args;

  return useMemo(() => {
    const period: Period = periodForCards;

    const labels = getRollingDashboardLabels(period);
    const currentLabel = labels[labels.length - 1] ?? '';
    const windowLabels = labels;

    const req: ChartRequest = {
      period,
      selectedBars: ['ordersBar', 'ordersValueBar', 'returnsBar', 'toShipBar', 'shippedBar', 'purchasesValueBar'],
      selectedChannels: ['Online', 'Store'],
      byRep: false,
      selectedReps: [],
      allReps: [],
      orientation: 'vertical',
    };

    const buckets = dataProvider.getStandardBuckets(req, labels);

    const sumByLabels = (labelsPick: string[], allLabels: string[], series: number[]) => {
      const idx = new Map<string, number>();
      allLabels.forEach((l, i) => idx.set(l, i));
      return labelsPick.reduce((acc, l) => acc + (series[idx.get(l) ?? -1] ?? 0), 0);
    };

    const pack = (online: number, store: number) => ({ online, store, total: online + store });

    // "Current" bucket value (today / this week / this month)
    const ordersCurrentOnline = sumByLabels([currentLabel], labels, buckets.ordersOnline);
    const ordersCurrentStore = sumByLabels([currentLabel], labels, buckets.ordersStore);
    const valueCurrentOnline = sumByLabels([currentLabel], labels, buckets.ordersValueOnline);
    const valueCurrentStore = sumByLabels([currentLabel], labels, buckets.ordersValueStore);
    const returnsCurrentOnline = sumByLabels([currentLabel], labels, buckets.returnsOnline);
    const returnsCurrentStore = sumByLabels([currentLabel], labels, buckets.returnsStore);

    // Window totals (last 7 days / last 30 days / last 12 months)
    const ordersWindowOnline = sumByLabels(windowLabels, labels, buckets.ordersOnline);
    const ordersWindowStore = sumByLabels(windowLabels, labels, buckets.ordersStore);
    const valueWindowOnline = sumByLabels(windowLabels, labels, buckets.ordersValueOnline);
    const valueWindowStore = sumByLabels(windowLabels, labels, buckets.ordersValueStore);
    const returnsWindowOnline = sumByLabels(windowLabels, labels, buckets.returnsOnline);
    const returnsWindowStore = sumByLabels(windowLabels, labels, buckets.returnsStore);

    // Avg/day divisor for the chosen period window
    let divisorDays = 1;
    if (period === 'Week') divisorDays = 7;
    else if (period === 'Month') divisorDays = 30;
    else divisorDays = Math.max(1, windowLabels.map(l => daysInMonthForLabel(l)).reduce((a, b) => a + b, 0));

    const now = new Date();
    const cutoff = startOfDayLocal(addMonthsLocal(now, -3));

    const purchasingLast3MonthsSEK = (dashboardPurchasing ?? []).reduce((acc: number, po: DashboardPurchasingRow) => {
      const when = po?.PurchaseOrderCreated;
      if (!when) return acc;
      const dt = new Date(when);
      if (!Number.isFinite(+dt)) return acc;
      if (dt < cutoff || dt > now) return acc;
      const v = Number(po?.OrderValueInDefaultCurreny ?? 0) || 0;
      return acc + v;
    }, 0);

    const monthStart = startOfMonthLocal(now);

    let shippedThisMonth = 0;
    let toShipOpen = 0;

    (dashboardDispatched ?? []).forEach((row: DashboardDispatchedRow) => {
      const dt = new Date(row.Day);
      if (!Number.isFinite(+dt)) return;
      if (dt < monthStart || dt > now) return;
      if (isOnlineWarehouse(Number(row.WarehouseId))) shippedThisMonth += Number(row.CountOrders ?? 0) || 0;
    });

    (dashboardToShip ?? []).forEach((row: DashboardToShipRow) => {
      const warehouseId = Number(row.WarehouseId);
      if (!isOnlineWarehouse(warehouseId) && !isStoreWarehouse(warehouseId)) return;
      toShipOpen += Number(row.CountOrders ?? 0) || 0;
    });

    return {
      range: { todayLabel: currentLabel, last7Labels: windowLabels }, // keep field name for compatibility
      orders: {
        today: pack(ordersCurrentOnline, ordersCurrentStore),
        week: pack(ordersWindowOnline, ordersWindowStore),
        avgPerDay: pack(ordersWindowOnline / divisorDays, ordersWindowStore / divisorDays),
      },
      orderValue: {
        today: pack(valueCurrentOnline, valueCurrentStore),
        week: pack(valueWindowOnline, valueWindowStore),
        avgPerDay: pack(valueWindowOnline / divisorDays, valueWindowStore / divisorDays),
      },
      returns: {
        today: pack(returnsCurrentOnline, returnsCurrentStore),
        week: pack(returnsWindowOnline, returnsWindowStore),
        avgPerDay: pack(returnsWindowOnline / divisorDays, returnsWindowStore / divisorDays),
      },
      purchasingLast3MonthsSEK,
      shipping: { shippedThisMonth, toShipOpen },
    };
  }, [dataProvider, dashboardPurchasing, dashboardDispatched, dashboardToShip, periodForCards]);
};